/**
 * Calendar Screen
 *
 * Main calendar tab displaying device calendar events.
 * Refactored to use extracted hooks following OOP principles.
 */

import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BackHandler,
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRouter, useLocalSearchParams } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';

import { CalendarEventModal } from '@/components/calendar/event-modal';
import { CalendarPermissionPrompt } from '@/components/calendar/CalendarPermissionPrompt';
import { CalendarSideDrawer, type CalendarViewMode } from '@/components/calendar/CalendarSideDrawer';
import { CalendarSkeleton } from '@/components/ui/CalendarSkeleton';
import { CalendarOnboardingFlow } from '@/components/onboarding/CalendarOnboardingFlow';
import { CalendarManagementScreen } from '@/components/calendar/CalendarManagementScreen';
import { MonthlyCalendarScreen } from '@/components/calendar/MonthlyCalendarScreen';
import { FamilyResourcesCalendarView } from '@/components/calendar/FamilyResourcesCalendarView';
import { WeeklyAgendaView, type WeeklyAgendaViewHandle } from '@/components/calendar/WeeklyAgendaView';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useCalendarOnboarding } from '@/hooks/use-calendar-onboarding';
import { useCalendarScreenState, buildEventWindow } from '@/hooks/use-calendar-screen-state';
import { useCalendarPrefetch } from '@/hooks/use-calendar-prefetch';
import { useCalendarScreenHandlers } from '@/hooks/use-calendar-screen-handlers';
import { useFamilyResourcesCalendar } from '@/hooks/use-family-resources-calendar';
import { useMeDisplayName } from '@/hooks/use-me-display-name';
import { useWeeklyAgendaCalendar } from '@/hooks/use-weekly-agenda-calendar';
import { useScopedManagers } from '@/hooks/use-scoped-managers';
import { useResolvedDefaultCalendarId } from '@/hooks/use-resolved-default-calendar-id';
import { FamilyMemberEvents } from '@/lib/family/FamilyMemberEvents';
import { CalendarMappingEvents } from '@/lib/calendar/CalendarMappingEvents';
import { useSupabaseAuth } from '@/lib/supabase';
import { LocalDateFormatter } from '@/lib/formatters/LocalDateFormatter';
import { AvatarNavigationHandler } from '@/lib/calendar/AvatarNavigationHandler';
import { ViewHistoryManager } from '@/lib/calendar/ViewHistoryManager';
import type { CalendarEvent } from '@/types/events';
import type { FamilyMemberResource } from '@/types/family-resource';

// Helper functions
const toDateKey = (date: Date): string => {
  return LocalDateFormatter.toDateKey(date);
};

const parseDateString = (value: string): Date => {
  return LocalDateFormatter.fromDateKey(value);
};

const isDateWithinWindow = (dateString: string, window: any | null): boolean => {
  if (!window) {
    return false;
  }
  const date = parseDateString(dateString);
  return date >= window.start && date <= window.end;
};

const buildCacheKey = (calendarIds: string[], window: any): string => {
  const sorted = [...calendarIds].sort().join('|');
  return `${sorted}::${window.key}`;
};

export default function CalendarScreen() {
  const colorScheme = useColorScheme();
  const { session, user } = useSupabaseAuth();
  const navigation = useNavigation();
  const router = useRouter();
  const { openDrawer } = useLocalSearchParams<{ openDrawer?: string }>();
  const { displayName: meDisplayName } = useMeDisplayName();

  // Onboarding flow
  const {
    showOnboarding,
    handleOnboardingComplete,
    handleOnboardingSkip,
    triggerOnboarding,
  } = useCalendarOnboarding();

  // Extract all state using custom hook
  const state = useCalendarScreenState({
    accessToken: session?.access_token ?? null,
    colorScheme,
  });

  // Scoped storage managers
  const { familyMembersStore, calendarMemberMappingStore, isReady: storageReady } = useScopedManagers();

  // Extract prefetch logic
  const { loadEventsFromDevice } = useCalendarPrefetch({
    manager: state.manager,
    selectedCalendarIds: state.selectedCalendarIds,
    buildEventWindow,
    eventsCacheRef: state.eventsCacheRef,
    latestEventRequestRef: state.latestEventRequestRef,
    selectedDateRef: state.selectedDateRef,
    activeEventWindowRef: state.activeEventWindowRef,
    isLoadingDataRef: state.isLoadingDataRef,
    setSections: state.setSections,
    setFlatItems: state.setFlatItems,
    setSelectedDate: state.setSelectedDate,
    setActiveEventWindow: state.setActiveEventWindow,
    setError: state.setError,
    setIsLoading: state.setIsLoading,
    setIsSilentLoading: state.setIsSilentLoading,
  });

  // Track displayed month for header
  const [, setDisplayedMonth] = useState<Date>(new Date());

  // Key to force Calendar component remount when Today is pressed
  const [calendarKey, setCalendarKey] = useState(0);

  // Pre-filled time from double-tap on day view grid
  const [preselectedTime, setPreselectedTime] = useState<string | null>(null);
  // Pre-filled target calendar (used by weekly double-tap to default to the column's member calendar)
  const [preselectedCalendarId, setPreselectedCalendarId] = useState<string | null>(null);

  const writableCalendars = useMemo(
    () => state.deviceCalendars.filter((cal) => cal.allowsModifications),
    [state.deviceCalendars],
  );
  const storedDefaultCalendarId = useResolvedDefaultCalendarId(writableCalendars);

  // Top-level view mode (month or week) - persisted
  const [parentViewMode, setParentViewMode] = useState<CalendarViewMode>('month');
  // Whether we're drilled down into the day (resources) view
  const [isDayDrillDown, setIsDayDrillDown] = useState(false);

  // View navigation history for back button
  const viewHistoryRef = useRef(new ViewHistoryManager());

  // Ref for weekly agenda view (scroll to day)
  const weeklyAgendaViewRef = useRef<WeeklyAgendaViewHandle>(null);

  // Trigger counter for scrolling to today in week view
  const [scrollToTodayTrigger, setScrollToTodayTrigger] = useState(0);

  // Date tapped on the monthly grid — gently highlighted when landing in week view
  const [weekHighlightDateKey, setWeekHighlightDateKey] = useState<string | null>(null);

  // Scroll target for week view — unlike the highlight, NOT cleared on week
  // swipe, so the grid's initialScrollDateKey effect doesn't re-fire toward
  // today when the highlight clears.
  const [weekInitialScrollKey, setWeekInitialScrollKey] = useState<string | null>(null);

  // Load saved preferences on mount
  useEffect(() => {
    AsyncStorage.getItem('@calendar_view_mode').then(saved => {
      if (saved && (saved === 'month' || saved === 'week' || saved === 'day')) {
        setParentViewMode(saved as CalendarViewMode);
        if (saved === 'day') {
          setIsDayDrillDown(true);
        }
      }
    });
  }, []);

  // Handle side drawer view mode change (month, week, or day)
  const handleViewModeChange = useCallback((mode: CalendarViewMode) => {
    // Push current state to history before switching
    viewHistoryRef.current.push({
      viewMode: isDayDrillDown ? 'day' : parentViewMode,
      date: state.selectedDate,
      isDayDrillDown,
    });
    setParentViewMode(mode);
    if (mode === 'day') {
      setIsDayDrillDown(true);
    } else {
      setIsDayDrillDown(false);
    }
    AsyncStorage.setItem('@calendar_view_mode', mode);
  }, [isDayDrillDown, parentViewMode, state.selectedDate]);

  // Handler to navigate to family screen from side drawer
  const handleFamilyMembers = useCallback(() => {
    state.setShowCalendarSelector(false);
    router.navigate({ pathname: '/(tabs)/family', params: { from: 'calendar' } });
  }, [state, router]);

  // Handler to trigger calendar setup from side drawer
  const handleCalendarSetup = useCallback(() => {
    state.setShowCalendarSelector(false);
    setTimeout(() => triggerOnboarding(), 300);
  }, [triggerOnboarding, state]);

  // Handle onboarding completion with visibility preferences
  const handleOnboardingFlowComplete = useCallback(
    async (_linkedIds: string[], visibleCalendars: import('@/lib/calendar/DeviceCalendarService').DeviceCalendar[]) => {
      const visibleIds = visibleCalendars.map((c) => c.id);

      // Apply visibility selections via preferences manager
      if (state.preferencesManager) {
        const visibleIdSet = new Set(visibleIds);
        const updates = state.deviceCalendars.map((cal) => ({
          calendarId: cal.id,
          isVisible: visibleIdSet.has(cal.id),
        }));
        await state.preferencesManager.batchUpdatePreferences(updates);
        await state.preferencesManager.markSetupCompleted();
      }

      // Activate the picked calendars as the live selection so events
      // load immediately for the user's calendars.
      await state.manager.saveCalendarSelection(visibleIds);
      state.setSelectedCalendarIds(visibleIds);

      handleOnboardingComplete();
    },
    [
      state.preferencesManager,
      state.deviceCalendars,
      state.manager,
      state.setSelectedCalendarIds,
      handleOnboardingComplete,
    ],
  );

  // Open side drawer when navigated with openDrawer param
  useEffect(() => {
    if (openDrawer === 'true') {
      state.setShowCalendarSelector(true);
      // Clear the param so it doesn't re-trigger
      router.setParams({ openDrawer: undefined } as any);
    }
  }, [openDrawer, state.setShowCalendarSelector, router]);

  // Extract all handlers using custom hook
  const handlers = useCalendarScreenHandlers({
    manager: state.manager,
    preferencesManager: state.preferencesManager,
    calendarSelectionTimeoutRef: state.calendarSelectionTimeoutRef,
    setSelectedDate: state.setSelectedDate,
    setDeviceCalendars: state.setDeviceCalendars,
    setSelectedCalendarIds: state.setSelectedCalendarIds,
    setPreferences: state.setPreferences,
    setHasPermission: state.setHasPermission,
    setIsLoading: state.setIsLoading,
    setIsRequestingPermission: state.setIsRequestingPermission,
    setError: state.setError,
    setEventModalMode: state.setEventModalMode,
    setSelectedEvent: state.setSelectedEvent,
    setShowEventModal: state.setShowEventModal,
    setIsSubmittingEvent: state.setIsSubmittingEvent,
    setIsRefreshing: state.setIsRefreshing,
    setDisplayedMonth,
    incrementCalendarKey: () => setCalendarKey(prev => prev + 1),
    selectedCalendarIds: state.selectedCalendarIds,
    deviceCalendars: state.deviceCalendars,
    selectedEvent: state.selectedEvent,
    eventModalMode: state.eventModalMode,
    userEmail: user?.email,
    loadEventsFromDevice,
  });

  // Handle date selection in monthly or weekly view - drills into day view
  const handleDateDrillDown = useCallback((dateString: string) => {
    // Push current state to history before drilling down
    viewHistoryRef.current.push({
      viewMode: parentViewMode,
      date: state.selectedDate,
      isDayDrillDown,
    });
    handlers.handleDateSelect(dateString);
    setIsDayDrillDown(true);
  }, [handlers, parentViewMode, state.selectedDate, isDayDrillDown]);

  // Weekly empty cell double-tap: open create-event modal pre-filled for that day/member at 8–9am
  const handleWeeklyCellCreate = useCallback((dateKey: string, memberId: string) => {
    const mapping = state.calendarMappings.find(m => m.memberIds.includes(memberId));
    const targetCalendarId = mapping?.calendarId ?? null;
    state.setSelectedDate(dateKey);
    setPreselectedTime('08:00');
    setPreselectedCalendarId(targetCalendarId);
    handlers.handleFABPress();
  }, [state, handlers]);

  // Transform events for monthly calendar view with label filtering
  const allEventsRaw = useMemo((): CalendarEvent[] => {
    // Convert sections to flat array of CalendarEvent
    const events = state.sections.flatMap(section =>
      section.data.flatMap(slot => slot.events)
    );

    // Skip label filtering if:
    // 1. No labels selected (selectedLabelIds empty)
    // 2. No mappings exist (nothing to filter by)
    // 3. All labels are selected (show everything - default state)
    const allLabelsSelected = state.familyMembers.length > 0 &&
      state.selectedLabelIds.length >= state.familyMembers.length;

    if (state.selectedLabelIds.length === 0 ||
        state.calendarMappings.length === 0 ||
        allLabelsSelected) {
      return events;
    }

    // Get calendar IDs that are mapped to selected family members
    const selectedMemberCalendarIds = new Set<string>();
    state.calendarMappings.forEach(mapping => {
      const hasSelectedMember = mapping.memberIds.some(
        memberId => state.selectedLabelIds.includes(memberId)
      );
      if (hasSelectedMember) {
        selectedMemberCalendarIds.add(mapping.calendarId);
      }
    });

    // Also include calendars that have no mappings (unmapped calendars show all events)
    const mappedCalendarIds = new Set(state.calendarMappings.map(m => m.calendarId));

    return events.filter(event => {
      // If this calendar has no mapping, always show the event
      if (!mappedCalendarIds.has(event.deviceCalendarId)) {
        return true;
      }
      // If this calendar is mapped to a selected member, show the event
      return selectedMemberCalendarIds.has(event.deviceCalendarId);
    });
  }, [state.sections, state.selectedLabelIds, state.calendarMappings, state.familyMembers, state.selectedCalendarIds]);

  // Handle date change from resources view
  const handleResourcesDateChange = useCallback((date: Date) => {
    const dateKey = toDateKey(date);
    state.setSelectedDate(dateKey);
  }, [state]);

  // Load family members and mappings
  const loadFamilyData = useCallback(async () => {
    if (!familyMembersStore || !calendarMemberMappingStore) {
      return;
    }

    try {
      const [members, mappings] = await Promise.all([
        familyMembersStore.getAll(),
        calendarMemberMappingStore.getAll(),
      ]);
      state.setFamilyMembers(members);
      state.setCalendarMappings(mappings);

      // Initialize selectedLabelIds with all member IDs if empty
      if (state.selectedLabelIds.length === 0 && members.length > 0) {
        state.setSelectedLabelIds(members.map(m => m.id));
      }
    } catch (error) {
      console.error('[CalendarScreen] Failed to load family data:', error);
    }
  }, [
    familyMembersStore,
    calendarMemberMappingStore,
    state.setFamilyMembers,
    state.setCalendarMappings,
    state.selectedLabelIds.length,
    state.setSelectedLabelIds,
  ]);

  // Load family data when storage is ready
  useEffect(() => {
    if (storageReady) {
      loadFamilyData();
    }
  }, [storageReady, loadFamilyData]);

  // Subscribe to family member changes (add/update/delete from Family tab)
  useEffect(() => {
    const unsubscribe = FamilyMemberEvents.subscribe(() => {
      loadFamilyData();
    });
    return unsubscribe;
  }, [loadFamilyData]);

  // Subscribe to calendar-member mapping changes (link/unlink from calendar linking screen)
  useEffect(() => {
    const unsubscribe = CalendarMappingEvents.subscribe(() => {
      loadFamilyData();
    });
    return unsubscribe;
  }, [loadFamilyData]);

  // Family resources calendar state - sync with main calendar date
  const familyResourcesCalendar = useFamilyResourcesCalendar({
    familyMembers: state.familyMembers,
    calendarMappings: state.calendarMappings,
    allEvents: allEventsRaw,
    initialDate: parseDateString(state.selectedDate),
    onDateChange: handleResourcesDateChange,
    meDisplayName,
    selectedLabelIds: state.selectedLabelIds,
  });

  // Filter family members based on selected labels for weekly view
  const filteredFamilyMembers = useMemo(() => {
    const allSelected = state.selectedLabelIds.length === 0 ||
      state.selectedLabelIds.length >= state.familyMembers.length;
    if (allSelected) return state.familyMembers;
    return state.familyMembers.filter(m => state.selectedLabelIds.includes(m.id));
  }, [state.familyMembers, state.selectedLabelIds]);

  // Sync selectedDate when week changes (for event window prefetching)
  const handleWeekChange = useCallback((mondayDateKey: string) => {
    state.setSelectedDate(mondayDateKey);
    setWeekHighlightDateKey(null);
  }, [state]);

  // Weekly agenda calendar state
  const weeklyAgenda = useWeeklyAgendaCalendar({
    allEvents: allEventsRaw,
    familyMembers: filteredFamilyMembers,
    calendarMappings: state.calendarMappings,
    meMemberId: familyResourcesCalendar.meMemberId,
    onWeekChange: handleWeekChange,
  });

  // Monthly date tap: switch to weekly view at that date (instead of drilling to day)
  const handleMonthlySelect = useCallback((dateString: string) => {
    viewHistoryRef.current.push({
      viewMode: parentViewMode,
      date: state.selectedDate,
      isDayDrillDown,
    });
    setWeekHighlightDateKey(dateString);
    setWeekInitialScrollKey(dateString);
    handlers.handleDateSelect(dateString);
    setParentViewMode('week');
    setIsDayDrillDown(false);
    weeklyAgenda.goToDate(parseDateString(dateString));
  }, [handlers.handleDateSelect, parentViewMode, state.selectedDate, isDayDrillDown, weeklyAgenda]);

  // Handle event press from weekly view
  const handleWeeklyEventPress = useCallback((event: CalendarEvent) => {
    handlers.handleEventPress(event);
  }, [handlers]);

  // Handle avatar press - navigate to profile
  const handleAvatarPress = useCallback((resource: FamilyMemberResource) => {
    AvatarNavigationHandler.navigateToProfile(router, resource);
  }, [router]);

  // Handle event press from family resources view - extract original ID from duplicated events
  const handleResourceEventPress = useCallback((eventId: string) => {
    // FamilyResourceMapper creates IDs like `${originalId}_${memberId}` for multi-member events
    // Extract original event ID by removing the member suffix
    const originalId = eventId.includes('_')
      ? eventId.substring(0, eventId.lastIndexOf('_'))
      : eventId;

    const event = allEventsRaw.find(e => e.id === originalId);
    if (event) {
      handlers.handleEventPress(event);
    } else {
      console.warn('[FamilyResourcesCalendarView] Event not found:', originalId);
    }
  }, [allEventsRaw, handlers]);

  // Week view today press: jump to today's week AND scroll to today's row
  const handleWeekTodayPress = useCallback(() => {
    weeklyAgenda.goToToday();
    setScrollToTodayTrigger(prev => prev + 1);
    setWeekHighlightDateKey(null);
    setWeekInitialScrollKey(null);
  }, [weeklyAgenda.goToToday]);

  // Header tap: day view → navigate up to week view
  const handleDayHeaderTextPress = useCallback(() => {
    viewHistoryRef.current.push({
      viewMode: isDayDrillDown ? 'day' : parentViewMode,
      date: state.selectedDate,
      isDayDrillDown,
    });
    setParentViewMode('week');
    setIsDayDrillDown(false);
    weeklyAgenda.goToDate(parseDateString(state.selectedDate));
  }, [isDayDrillDown, parentViewMode, state.selectedDate, weeklyAgenda.goToDate]);

  // Header tap: week view → navigate up to month view
  const handleWeekHeaderTextPress = useCallback(() => {
    viewHistoryRef.current.push({
      viewMode: parentViewMode,
      date: state.selectedDate,
      isDayDrillDown,
    });
    setParentViewMode('month');
    setIsDayDrillDown(false);
  }, [parentViewMode, state.selectedDate, isDayDrillDown]);

  // Handle Android hardware back button - restore previous view from history stack
  useFocusEffect(
    useCallback(() => {
      const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
        const previousEntry = viewHistoryRef.current.pop();
        if (previousEntry) {
          setParentViewMode(previousEntry.viewMode);
          setIsDayDrillDown(previousEntry.isDayDrillDown);
          state.setSelectedDate(previousEntry.date);
          if (previousEntry.viewMode === 'week') {
            weeklyAgenda.goToDate(parseDateString(previousEntry.date));
          }
          return true;
        }
        // No history - allow default back behavior
        return false;
      });
      return () => backHandler.remove();
    }, [weeklyAgenda.goToDate, state.setSelectedDate])
  );

  // Handle month change for prefetching
  const handleMonthChange = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = '01'; // Always use first day of month for consistency
    const dateString = `${year}-${month}-${day}`;

    // Update displayed month for header
    setDisplayedMonth(date);

    // Update selectedDate to sync Calendar's current prop
    state.setSelectedDate(dateString);

    // Trigger prefetch for the new month
    loadEventsFromDevice({ targetDate: dateString, silent: true });
  }, [loadEventsFromDevice, state.setSelectedDate]);

  // Sync refs with state
  useEffect(() => {
    state.selectedDateRef.current = state.selectedDate;
  }, [state.selectedDate, state.selectedDateRef]);

  useEffect(() => {
    state.activeEventWindowRef.current = state.activeEventWindow;
  }, [state.activeEventWindow, state.activeEventWindowRef]);

  // Sync week view to selectedDate when switching to week mode
  useEffect(() => {
    if (parentViewMode === 'week' && !isDayDrillDown) {
      weeklyAgenda.goToDate(parseDateString(state.selectedDate));
    }
  }, [parentViewMode, isDayDrillDown, state.selectedDate, weeklyAgenda.goToDate]);

  // Refresh events silently when view mode changes
  useEffect(() => {
    if (state.hasPermission && state.selectedCalendarIds.length > 0) {
      loadEventsFromDevice({ forceRefresh: true, silent: true });
    }
  }, [parentViewMode, state.hasPermission, state.selectedCalendarIds.length, loadEventsFromDevice]);

  // Update manager when session changes
  useEffect(() => {
    state.manager.setAccessToken(session?.access_token ?? null);
  }, [state.manager, session?.access_token]);

  // Check permissions on mount
  useEffect(() => {
    handlers.checkCalendarPermission();
  }, [handlers.checkCalendarPermission]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (state.calendarSelectionTimeoutRef.current) {
        clearTimeout(state.calendarSelectionTimeoutRef.current);
      }
    };
  }, [state.calendarSelectionTimeoutRef]);

  // Handle tab press to jump to today if already focused
  useEffect(() => {
    // expo-router's navigation prop is typed as a Stack navigator, but at runtime
    // it's a tab navigator that emits 'tabPress'. Cast through unknown to access it.
    const tabNav = navigation as unknown as {
      addListener: (event: 'tabPress', handler: (e: { preventDefault: () => void }) => void) => () => void;
      isFocused: () => boolean;
    };
    const unsubscribe = tabNav.addListener('tabPress', (e) => {
      if (tabNav.isFocused()) {
        e.preventDefault();
        handlers.handleTodayPress();
      }
    });

    return unsubscribe;
  }, [navigation, handlers.handleTodayPress]);

  // Load events when calendar selection changes
  useEffect(() => {
    if (state.hasPermission && state.selectedCalendarIds.length > 0) {
      loadEventsFromDevice();
    }
  }, [state.hasPermission, state.selectedCalendarIds, loadEventsFromDevice]);

  // Handle date selection outside current window
  useEffect(() => {
    if (!state.hasPermission || state.selectedCalendarIds.length === 0) {
      return;
    }

    // If date is within current window, no need to load
    if (isDateWithinWindow(state.selectedDate, state.activeEventWindowRef.current)) {
      return;
    }

    // Date is outside window - check cache synchronously for instant response
    const newWindow = buildEventWindow(state.selectedDate);
    const cacheKey = buildCacheKey(state.selectedCalendarIds, newWindow);
    const cached = state.eventsCacheRef.current.get(cacheKey);

    if (cached) {
      // Cache hit - update instantly without async overhead
      state.setSections(cached.sections);
      state.setFlatItems(cached.flatItems);
      state.setActiveEventWindow(newWindow);
      state.activeEventWindowRef.current = newWindow;
      state.setError(null);
      state.setIsLoading(false);
    } else {
      // Cache miss - trigger async load
      loadEventsFromDevice({ targetDate: state.selectedDate, silent: true });
    }
  }, [
    state.selectedDate,
    state.hasPermission,
    state.selectedCalendarIds,
    state.activeEventWindowRef,
    state.eventsCacheRef,
    state.setSections,
    state.setFlatItems,
    state.setActiveEventWindow,
    state.setError,
    state.setIsLoading,
    loadEventsFromDevice,
  ]);

  // Reload events when tab gains focus
  useFocusEffect(
    useCallback(() => {
      if (state.hasPermission) {
        loadEventsFromDevice({ forceRefresh: true });
      }
    }, [state.hasPermission, loadEventsFromDevice]),
  );

  // Show platform not supported message for web
  if (!state.manager.canAccessCalendar()) {
    return (
      <SafeAreaView
        style={[styles.screen, { backgroundColor: state.backgroundColor }]}
        edges={['bottom']}
      >
        <View style={styles.errorState}>
          <Text style={[styles.errorText, { color: state.textColor }]}>
            Calendar access requires the native app
          </Text>
          <Text style={[styles.errorMessage, { color: state.textColor }]}>
            Please use the Android or iOS app to access your calendar.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.screen, { backgroundColor: state.backgroundColor }]}
      edges={['bottom']}
    >
      <View style={styles.contentWrapper}>
        <View style={styles.mainContent}>
          {state.hasPermission === null || (state.isLoading && state.hasPermission) ? (
            <CalendarSkeleton itemCount={5} />
          ) : !state.hasPermission ? (
            <CalendarPermissionPrompt
              onRequestPress={handlers.requestPermission}
              isRequesting={state.isRequestingPermission}
              onStartSetup={triggerOnboarding}
            />
          ) : (
            <View style={{ flex: 1 }}>
              {state.error ? (
                <View style={styles.errorState}>
                  <Text style={[styles.errorText, { color: state.textColor }]}>
                    Unable to load events
                  </Text>
                  <Text style={[styles.errorMessage, { color: state.textColor }]}>
                    {state.error}
                  </Text>
                  <TouchableOpacity
                    style={[
                      styles.primaryActionButton,
                      { backgroundColor: state.accentColor },
                    ]}
                    onPress={handlers.handleRefresh}
                  >
                    <Text style={[styles.primaryActionButtonText]}>Retry</Text>
                  </TouchableOpacity>
                </View>
              ) : state.selectedCalendarIds.length === 0 && state.sections.length === 0 && !state.isLoading ? (
                <View style={{ flex: 1 }}>
                  <View
                    style={[
                      styles.emptyStateHeader,
                      {
                        backgroundColor: state.palette.cardBackground,
                        borderBottomColor: state.palette.borderMuted,
                      },
                    ]}
                  >
                    <TouchableOpacity
                      style={styles.menuIconButton}
                      onPress={() => state.setShowCalendarSelector((prev) => !prev)}
                      activeOpacity={0.7}
                    >
                      <Ionicons
                        name={state.showCalendarSelector ? 'close' : 'menu'}
                        size={24}
                        color={state.palette.tint}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.noEventsState}>
                    <Image
                      source={require('@/assets/images/fim-sleep-transparent.png')}
                      style={styles.emptyImage}
                      resizeMode="contain"
                    />
                    <Text style={[styles.emptyTitle, { color: state.textColor }]}>
                      No calendar selected
                    </Text>
                    <Text style={[styles.emptySubtitle, { color: state.textColor }]}>
                      Open the menu to set up your calendar
                    </Text>
                    <TouchableOpacity
                      style={[
                        styles.primaryActionButton,
                        { backgroundColor: state.accentColor },
                      ]}
                      onPress={handleCalendarSetup}
                    >
                      <Text style={styles.primaryActionButtonText}>Set up calendar</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (isDayDrillDown || parentViewMode === 'day') ? (
                <FamilyResourcesCalendarView
                  selectedDate={familyResourcesCalendar.selectedDate}
                  resources={familyResourcesCalendar.resources}
                  timedEvents={familyResourcesCalendar.timedEvents}
                  allDayEventsByMember={familyResourcesCalendar.allDayEventsByMember}
                  onDateChange={handleResourcesDateChange}
                  onEventPress={handleResourceEventPress}
                  onAvatarPress={handleAvatarPress}
                  onCreateEvent={(dateKey, time) => {
                    state.setSelectedDate(dateKey);
                    setPreselectedTime(time);
                    handlers.handleFABPress();
                  }}
                  accentColor={state.accentColor}
                  onMenuPress={() => state.setShowCalendarSelector((prev) => !prev)}
                  onTodayPress={handlers.handleTodayPress}
                  onAddEventPress={state.hasPermission ? () => {
                    setPreselectedTime(null);
                    handlers.handleFABPress();
                  } : undefined}
                  onHeaderTextPress={handleDayHeaderTextPress}
                  showCalendarSelector={state.showCalendarSelector}
                  onRefresh={handlers.handleRefresh}
                  isRefreshing={state.isRefreshing}
                />
              ) : parentViewMode === 'week' ? (
                <WeeklyAgendaView
                  ref={weeklyAgendaViewRef}
                  weekData={weeklyAgenda.weekData}
                  resources={familyResourcesCalendar.resources}
                  onPrevWeek={weeklyAgenda.goToPrevWeek}
                  onNextWeek={weeklyAgenda.goToNextWeek}
                  onTodayPress={handleWeekTodayPress}
                  onMenuPress={() => state.setShowCalendarSelector((prev) => !prev)}
                  onDayPress={handleDateDrillDown}
                  onEventPress={handleWeeklyEventPress}
                  onAvatarPress={handleAvatarPress}
                  onCreateEvent={handleWeeklyCellCreate}
                  onAddEventPress={state.hasPermission ? handlers.handleFABPress : undefined}
                  onHeaderTextPress={handleWeekHeaderTextPress}
                  showCalendarSelector={state.showCalendarSelector}
                  selectedDateKey={weekHighlightDateKey ?? undefined}
                  initialScrollDateKey={weekInitialScrollKey ?? LocalDateFormatter.getTodayDateString()}
                  scrollToTodayTrigger={scrollToTodayTrigger}
                />
              ) : (
                <MonthlyCalendarScreen
                  allEventsRaw={allEventsRaw}
                  selectedDate={state.selectedDate}
                  onDateSelect={handleMonthlySelect}
                  onMonthChange={handleMonthChange}
                  onRefresh={handlers.handleRefresh}
                  isRefreshing={state.isRefreshing}
                  accentColor={state.accentColor}
                  onMenuPress={() => state.setShowCalendarSelector((prev) => !prev)}
                  onTodayPress={handlers.handleTodayPress}
                  onAddEventPress={state.hasPermission ? handlers.handleFABPress : undefined}
                  showCalendarSelector={state.showCalendarSelector}
                  calendarKey={calendarKey}
                  calendarMappings={state.calendarMappings}
                  familyMembers={state.familyMembers}
                  meMemberId={familyResourcesCalendar.meMemberId}
                />
              )}
            </View>
          )}
        </View>

        {state.hasPermission && (
          <CalendarSideDrawer
            visible={state.showCalendarSelector}
            onClose={() => state.setShowCalendarSelector(false)}
            calendars={state.deviceCalendars}
            selectedIds={state.selectedCalendarIds}
            onChange={handlers.handleCalendarSelectionChange}
            onManageCalendars={() => {
              state.setShowCalendarSelector(false);
              state.setShowManagementScreen(true);
            }}
            viewMode={isDayDrillDown ? 'day' : parentViewMode}
            onViewModeChange={handleViewModeChange}
            onCalendarSetup={handleCalendarSetup}
            onFamilyMembers={handleFamilyMembers}
          />
        )}

      </View>

      <CalendarEventModal
        visible={state.showEventModal}
        mode={state.eventModalMode}
        initialEvent={state.eventModalMode === 'edit' ? state.selectedEvent : null}
        preselectedDate={state.eventModalMode === 'create' ? state.selectedDate : null}
        preselectedTime={state.eventModalMode === 'create' ? preselectedTime : null}
        writableCalendars={writableCalendars}
        allCalendars={state.deviceCalendars}
        defaultCalendarId={
          (state.eventModalMode === 'create' && preselectedCalendarId)
            ? preselectedCalendarId
            : (storedDefaultCalendarId ?? state.selectedCalendarIds[0])
        }
        familyMembers={state.familyMembers}
        calendarMappings={state.calendarMappings}
        onSave={handlers.handleEventSave}
        onDelete={handlers.handleEventDelete}
        onDismiss={() => {
          state.setShowEventModal(false);
          setPreselectedTime(null);
          setPreselectedCalendarId(null);
        }}
        onInstantSave={() => loadEventsFromDevice({ forceRefresh: true, silent: true })}
        isSubmitting={state.isSubmittingEvent}
      />

      <CalendarManagementScreen
        visible={state.showManagementScreen}
        onClose={() => state.setShowManagementScreen(false)}
        calendars={state.deviceCalendars}
        selectedIds={state.selectedCalendarIds}
        onChange={handlers.handleCalendarSelectionChange}
        familyMembers={state.familyMembers}
        selectedLabelIds={state.selectedLabelIds}
        onLabelChange={state.setSelectedLabelIds}
      />

      <CalendarOnboardingFlow
        visible={showOnboarding}
        calendars={state.deviceCalendars}
        onComplete={handleOnboardingFlowComplete}
        onSkip={handleOnboardingSkip}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
  },
  contentWrapper: {
    flex: 1,
    position: 'relative',
  },
  mainContent: {
    flex: 1,
  },
  errorState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 8,
  },
  errorText: {
    fontSize: 12,
    fontWeight: '600',
  },
  errorMessage: {
    fontSize: 8,
    opacity: 0.8,
    textAlign: 'center',
  },
  primaryActionButton: {
    marginTop: 12,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    alignItems: 'center',
  },
  primaryActionButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  noEventsState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    gap: 12,
  },
  emptyStateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  menuIconButton: {
    padding: 8,
  },
  emptyImage: {
    width: 100,
    height: 100,
  },
  emptyTitle: {
    fontSize: 12,
    fontWeight: '600',
  },
  emptySubtitle: {
    fontSize: 8,
    textAlign: 'center',
    opacity: 0.7,
  },
});
